// Builds the two model tables every visual reads from:
//
//   asset_items    one row per RDrive Item (installation, commissioning,
//                  inspection ...) with normalised, typed columns plus the
//                  physical-asset key it belongs to.
//   unique_assets  one row per physical asset (BI-Asset-ID): the current
//                  record's status/location, "latest non-null wins" register
//                  fields and lifecycle aggregates.
//
// Both are materialised once per data load (CREATE OR REPLACE TABLE), so the
// cross-filter and every chart query hit a plain table instead of re-deriving
// window functions. Everything runs through conn.queryRaw so failures throw
// instead of being swallowed by the isDataLoaded gate in DuckDBService.
import { ASSET_FIELDS, castExpr, nullExpr } from './assetFields';

// Values customers type into an Asset ID field when they do not have one yet.
const PLACEHOLDER_IDS = ['N/A', 'NA', 'TBC', 'TBD', '-', 'NONE', 'NULL', 'UNKNOWN'];

const rows = (result) => (result && result.toArray ? result.toArray().map((row) => row.toJSON()) : []);

async function listColumns(conn, table) {
  const result = await conn.queryRaw(
    `SELECT column_name FROM information_schema.columns WHERE table_name = '${table}'`
  );
  return rows(result).map((row) => row.column_name);
}

// The pivot is served by the issues reporting route, so live projects carry
// Issue*-prefixed core columns while the demo generator uses Asset*. Detect
// rather than assume.
export function detectPrefix(columns) {
  if (columns.includes('AssetStatusId')) return 'Asset';
  if (columns.includes('IssueStatusId')) return 'Issue';
  if (columns.includes('ItemStatusId')) return 'Item';
  throw new Error('assets table has no recognised core columns (expected AssetStatusId or IssueStatusId)');
}

// Lookup tables the model and the grid join to. Requests are allSettled, so a
// failed lookup must not break the CTAS - create an empty shell instead.
export async function ensureLookupTables(conn) {
  await conn.queryRaw(`
    CREATE TABLE IF NOT EXISTS status_history(
      SubItemId VARCHAR, StatusId VARCHAR, CreatedDate TIMESTAMP, CreatedByTeamId VARCHAR, CreatedByUserId VARCHAR);
    CREATE TABLE IF NOT EXISTS status_templates(
      Id VARCHAR, Title VARCHAR, Color VARCHAR, Sort INTEGER,
      IsDraft BOOLEAN, IsInitial BOOLEAN, IsTerminal BOOLEAN, IsPassed BOOLEAN);
    CREATE TABLE IF NOT EXISTS locations(Id VARCHAR, Title VARCHAR, DrawingId VARCHAR, X DOUBLE, Y DOUBLE);
    CREATE TABLE IF NOT EXISTS drawings(Id VARCHAR, Title VARCHAR, Url VARCHAR, ThumbnailUrl VARCHAR);
    CREATE TABLE IF NOT EXISTS users(Id VARCHAR, Name VARCHAR, Email VARCHAR);
  `);
}

export function buildAssetItemsSql({ prefix, columns }) {
  const has = (column) => columns.includes(column);
  const core = (name, alias) =>
    (has(`${prefix}${name}`) ? `a.${prefix}${name} AS ${alias}` : `CAST(NULL AS VARCHAR) AS ${alias}`);

  const teamAssigneeColumn = [`${prefix}TeamAssignee`, `${prefix}Assignee`].find(has);
  const teamAssigneeExpr = teamAssigneeColumn ? `a.${teamAssigneeColumn}` : 'CAST(NULL AS VARCHAR)';
  const userAssigneesExpr = has(`${prefix}UserAssignees`) ? `a.${prefix}UserAssignees` : 'CAST(NULL AS VARCHAR)';
  const lastUpdatedExpr = has(`${prefix}LastUpdated`)
    ? `TRY_CAST(CAST(a.${prefix}LastUpdated AS VARCHAR) AS TIMESTAMP)`
    : 'CAST(NULL AS TIMESTAMP)';

  const fieldExprs = ASSET_FIELDS
    .map((field) => `${has(field.tag) ? castExpr(field.kind, `a."${field.tag}"`) : nullExpr(field.kind)} AS ${field.alias}`)
    .join(',\n        ');

  const placeholders = PLACEHOLDER_IDS.map((value) => `'${value}'`).join(', ');

  return `
    CREATE OR REPLACE TABLE asset_items AS
    WITH raw AS (
      SELECT
        a.Id AS id,
        ${core('TemplateId', 'templateId')},
        ${core('ClientId', 'clientId')},
        ${core('LocationId', 'locationId')},
        ${core('StatusId', 'statusId')},
        ${core('CreatedByUserId', 'createdByUserId')},
        ${core('CreatedByTeamId', 'createdByTeamId')},
        ${core('ControlledByTeamId', 'controlledByTeamId')},
        ${teamAssigneeExpr} AS teamAssignee,
        ${userAssigneesExpr} AS userAssignees,
        ${lastUpdatedExpr} AS lastUpdated,
        ${fieldExprs}
      FROM assets a
    ),
    hist AS (
      SELECT
        sh.SubItemId AS id,
        MIN(TRY_CAST(CAST(sh.CreatedDate AS VARCHAR) AS TIMESTAMP)) AS firstStatusDate,
        MAX(TRY_CAST(CAST(sh.CreatedDate AS VARCHAR) AS TIMESTAMP)) FILTER (WHERE st.IsTerminal) AS lastTerminalDate
      FROM status_history sh
      LEFT JOIN status_templates st ON st.Id = sh.StatusId
      GROUP BY sh.SubItemId
    ),
    typed AS (
      SELECT
        r.*,
        COALESCE(h.firstStatusDate, r.lastUpdated) AS createdDate,
        h.firstStatusDate IS NOT NULL AS createdDateFromHistory,
        COALESCE(st.IsTerminal, FALSE) AS isClosed,
        CASE WHEN COALESCE(st.IsTerminal, FALSE) THEN COALESCE(h.lastTerminalDate, r.lastUpdated) END AS closedDate,
        NULLIF(UPPER(regexp_replace(TRIM(r.assetRefId), '\\s+', ' ', 'g')), '') AS normRefId
      FROM raw r
      LEFT JOIN hist h USING (id)
      LEFT JOIN status_templates st ON st.Id = r.statusId
    ),
    keyed AS (
      SELECT
        t.* EXCLUDE (normRefId),
        CASE WHEN t.normRefId IN (${placeholders}) THEN NULL ELSE t.normRefId END AS normRefId
      FROM typed t
    )
    SELECT
      k.* EXCLUDE (normRefId),
      COALESCE(k.normRefId, 'item:' || k.id) AS assetKey,
      k.normRefId IS NOT NULL AS hasAssetId,
      CASE
        WHEN k.inspectionDate IS NOT NULL OR k.rating IS NOT NULL OR k.passRate IS NOT NULL THEN 'inspection'
        WHEN k.commissionDate IS NOT NULL THEN 'commissioning'
        WHEN k.installDate IS NOT NULL THEN 'installation'
        ELSE 'record'
      END AS itemKind,
      COALESCE(
        k.inspectionDate,
        CASE WHEN k.isClosed AND (k.rating IS NOT NULL OR k.passRate IS NOT NULL) THEN CAST(k.closedDate AS DATE) END
      ) AS effectiveInspectionDate,
      CAST(ROW_NUMBER() OVER (
        PARTITION BY COALESCE(k.normRefId, 'item:' || k.id)
        ORDER BY k.createdDate DESC NULLS LAST, k.lastUpdated DESC NULLS LAST, k.id DESC
      ) AS INTEGER) AS itemSeq
    FROM keyed k;
  `;
}

export function buildUniqueAssetsSql() {
  const fieldAggregates = ASSET_FIELDS
    .map((field) => (field.alias === 'assetRefId'
      // The registration record's spelling is the one users know the asset by,
      // with stray whitespace collapsed so the label reads cleanly.
      ? `regexp_replace(TRIM(arg_max(assetRefId, itemSeq) FILTER (WHERE assetRefId IS NOT NULL)), '\\s+', ' ', 'g') AS assetRefId`
      // Latest non-null wins: an installation record carries the register
      // data, later inspections only what changed.
      : `arg_min(${field.alias}, itemSeq) FILTER (WHERE ${field.alias} IS NOT NULL) AS ${field.alias}`))
    .join(',\n        ');

  return `
    CREATE OR REPLACE TABLE unique_assets AS
    WITH agg AS (
      SELECT
        assetKey,
        ${fieldAggregates},
        CAST(COUNT(*) AS INTEGER) AS itemCount,
        CAST(COUNT(*) FILTER (WHERE isClosed) AS INTEGER) AS closedCount,
        CAST(COUNT(*) FILTER (WHERE effectiveInspectionDate IS NOT NULL) AS INTEGER) AS inspectionCount,
        MIN(effectiveInspectionDate) AS firstInspectionDate,
        MAX(effectiveInspectionDate) AS latestInspectionDate,
        MIN(itemSeq) FILTER (WHERE nextInspectionDate IS NOT NULL) AS nextDueSeq,
        MAX(lastUpdated) AS assetLastUpdated,
        bool_or(hasAssetId) AS hasAssetId,
        bool_or(createdDateFromHistory) AS createdDateFromHistory
      FROM asset_items
      GROUP BY assetKey
    ),
    cur AS (
      SELECT * FROM asset_items WHERE itemSeq = 1
    ),
    first_item AS (
      SELECT assetKey, id AS firstItemId, createdDate, createdByUserId, createdByTeamId
      FROM asset_items
      QUALIFY ROW_NUMBER() OVER (
        PARTITION BY assetKey ORDER BY createdDate ASC NULLS LAST, lastUpdated ASC NULLS LAST, id ASC
      ) = 1
    )
    SELECT
      a.assetKey,
      a.assetKey AS id,
      c.id AS currentItemId,
      c.clientId AS assetClientId,
      c.templateId AS assetTemplateId,
      c.statusId AS assetStatusId,
      c.locationId AS assetLocationId,
      c.controlledByTeamId AS assetControlledByTeamId,
      c.teamAssignee AS assetAssignee,
      c.userAssignees AS assetUserAssignees,
      c.isClosed,
      c.createdDate AS currentCreatedDate,
      f.firstItemId,
      f.createdDate,
      f.createdByUserId AS assetCreatedByUserId,
      f.createdByTeamId AS assetCreatedByTeamId,
      a.* EXCLUDE (assetKey),
      COALESCE(cst.IsTerminal AND cst.IsPassed, FALSE) AS isCommissioned,
      EXISTS (
        SELECT 1 FROM asset_items l
        WHERE l.assetKey = a.assetKey
          AND l.itemSeq < a.nextDueSeq
          AND l.effectiveInspectionDate IS NOT NULL
      ) AS inspectedSinceDue
    FROM agg a
    JOIN cur c USING (assetKey)
    JOIN first_item f USING (assetKey)
    LEFT JOIN status_templates cst ON cst.Id = c.statusId;
  `;
}

// Which aliases exist as pivot columns and which actually hold a value for at
// least one asset. Visuals that depend on optional data hide themselves when
// `populated` is false.
export async function detectConfiguredFields(conn, columns) {
  const probes = ASSET_FIELDS.map((field) => `COUNT(${field.alias}) > 0 AS ${field.alias}`).join(',\n      ');
  const result = await conn.queryRaw(`
    SELECT
      ${probes},
      COALESCE(bool_or(createdDateFromHistory), FALSE) AS statusHistory,
      COUNT(*) FILTER (WHERE assetLocationId IS NOT NULL) > 0 AS location
    FROM unique_assets
  `);
  const row = rows(result)[0] || {};
  const fields = {};
  ASSET_FIELDS.forEach((field) => {
    fields[field.alias] = {
      present: columns.includes(field.tag),
      populated: Boolean(row[field.alias]),
    };
  });
  return {
    fields,
    hasStatusHistory: Boolean(row.statusHistory),
    hasLocations: Boolean(row.location),
  };
}

async function modelCounts(conn) {
  const result = await conn.queryRaw(`
    SELECT
      (SELECT COUNT(*) FROM asset_items) AS items,
      (SELECT COUNT(*) FROM unique_assets) AS assets,
      (SELECT COUNT(*) FROM unique_assets WHERE NOT hasAssetId) AS orphans
  `);
  const row = rows(result)[0] || {};
  return {
    items: Number(row.items) || 0,
    assets: Number(row.assets) || 0,
    orphans: Number(row.orphans) || 0,
  };
}

export async function buildAssetModel(duckDBService) {
  const conn = duckDBService.conn;
  const started = performance.now();

  await ensureLookupTables(conn);

  const columns = await listColumns(conn, 'assets');
  if (columns.length === 0) {
    throw new Error('assets table is missing - nothing to model');
  }
  const prefix = detectPrefix(columns);

  await conn.queryRaw(buildAssetItemsSql({ prefix, columns }));
  await conn.queryRaw(buildUniqueAssetsSql());

  const counts = await modelCounts(conn);
  const configured = await detectConfiguredFields(conn, columns);

  console.log(
    `[assetModel] prefix=${prefix} items=${counts.items} assets=${counts.assets} orphans=${counts.orphans} ` +
    `statusHistory=${configured.hasStatusHistory} in ${Math.round(performance.now() - started)}ms`
  );

  return { prefix, columns, counts, ...configured };
}

// Shared tail of both the live and the demo load path: build the model,
// (re)seed the cross-filter, publish the detected fields, flip dataLoaded.
export async function finalizeDataLoad(store) {
  const duckDBService = store.getters.getDuckDBService;
  const model = await buildAssetModel(duckDBService);
  await store.state.filterContext.initializeFilterContext();
  store.commit('setAssetModel', { built: true, ...model });
  store.commit('setDataLoaded', true);
  return model;
}
